import { AgencyActivityTypesField, Breadcrumb, Heading, Page, Period, withHasPerm } from 'components';
import { CompanyField, CompanyTitle } from 'modules/core/company/components';
import { IndexTable } from 'components/ControlledTable';
import { Box, TableColumns } from '@ff-it/ui';
import { agencyScopeRequired } from 'core/permissions';
import { OutletOrServiceCategoryField } from 'modules/supplier/outletOrServiceCategory/OutletOrServiceCategoryField';
import type { EmbeddedEnterprise } from 'modules/core/types';
import type { EmbeddedCompany } from 'types';
import { Link } from 'react-router-dom';
import type { Vendor } from 'modules/supplier/vendor/types';
import type { EmbeddedOutletOrServiceCategory } from 'modules/supplier/outletOrServiceCategory/types';
import { OutletOrServiceCategoryTitle } from 'modules/supplier/outletOrServiceCategory/OutletOrServiceCategoryTitle';
import type { EmbeddedProduct } from 'modules/supplier/products';
import { TitleContainer } from 'components/TitleContainer';
import { VendorField } from 'modules/supplier/vendor/VendorField';
import { SelectField } from '@ff-it/form';

const columns: TableColumns<{
  id: number;
  rel: string;
  is_direct: boolean;
  agency: EmbeddedEnterprise;
  provider: EmbeddedCompany;
  agreement: {
    id: number;
    number: string;
    date_from: string;
    date_to: string | null;
  };
  vendor: Vendor | null;
  outlet_or_service_category: EmbeddedOutletOrServiceCategory | null;
  product: Pick<EmbeddedProduct, 'id' | 'name' | 'kind' | 'type' | 'disabled'> | null;
}> = [
  {
    key: 'agency.id',
    content: 'Proxy',
    render: ({ agency, is_direct }) =>
      is_direct ? <small className="text-muted">Direct</small> : <CompanyTitle {...agency.company} />,
  },
  {
    key: 'provider.id',
    content: 'Provider',
    render: ({ provider }) => <CompanyTitle {...provider} />,
    cellProps: { className: 'py-0' },
  },
  {
    key: 'agreement.id',
    content: 'Agreement',
    render: ({ agency, provider, agreement, rel }) => (
      <Box display="flex" alignItems="center">
        <TitleContainer
          title={
            <Link
              to={`/${agency.slug}/contacts/providers/${provider.parent || provider.id}/agreements/${agreement.id}`}
            >
              {agreement.number}
            </Link>
          }
          subtitle={rel}
        />

        <Box ml="auto" pl="2">
          <Period date_from={agreement.date_from} date_to={agreement.date_to} />
        </Box>
      </Box>
    ),
    cellProps: { className: 'py-0' },
  },
  {
    key: 'vendor.id',
    content: 'Vendor',
    render: ({ vendor }) => vendor?.name,
  },
  {
    key: 'outlet_or_service_category.id',
    content: 'Outlet or service category',
    render: ({ outlet_or_service_category }) =>
      outlet_or_service_category ? <OutletOrServiceCategoryTitle {...outlet_or_service_category} /> : null,
    cellProps: { className: 'py-0' },
  },

  {
    key: 'product.id',
    content: 'Product',
    render: ({ product }) =>
      product ? <TitleContainer title={product.name} disabled={product.disabled} subtitle={product.kind} /> : null,
    cellProps: { className: 'py-0' },
  },
  {
    key: 'product.type',
    content: 'Type',
    render: ({ product }) => (product ? product.type : null),
    width: '100px',
    cellProps: {
      textAlign: 'right',
    },
  },
];

const directOptions = [
  {
    value: 'true',
    label: 'Direct',
  },
  {
    value: 'false',
    label: 'Proxy',
  },
];

const Provided = withHasPerm(function Provided() {
  return (
    <Page>
      <Breadcrumb to={null}>Provided</Breadcrumb>
      <IndexTable
        before={<Heading title="Provided" />}
        columns={columns}
        url="supplier/provided"
        filterFields={
          <>
            <CompanyField name="provider" placeholder="Provider" simple includeBranches={true} />
            <VendorField name="vendor" placeholder="Vendor" simple className="ml-2" />
            <OutletOrServiceCategoryField
              name="outlet_or_service_category"
              simple
              placeholder="Outlet or service category"
              className="ml-2"
            />
            <AgencyActivityTypesField name="type" placeholder="Type" className="ml-2" />
            <SelectField name="direct" placeholder="Direct or proxy" options={directOptions} simple className="ml-2" />
          </>
        }
      />
    </Page>
  );
}, agencyScopeRequired);

export const route = {
  path: 'provided',
  element: <Provided />,
};
